/**
 * The void-copies banner: voided invoices whose QuickBooks copy is
 * still to be closed over there. Olive's push is create-only, so voiding
 * here never voids the copy, and the copy can keep charging the family
 * until a person voids it in QuickBooks. That is the loudest money risk
 * on the cockpit, so it leads the main column as a danger banner rather
 * than living in a column label and a rail count.
 *
 * Danger register: text-danger on pepper-soft (measured 5.53:1 in
 * src/index.css), role="alert". Danger, not risk: harm may already have
 * landed, and the obvious next click (void the copy over there with a
 * payment still applied) makes it worse.
 *
 * NO NUMBER IS INVENTED. `count` is the server's month-scoped
 * to_fix_count; `paymentCount` is derived from the rows on screen, and
 * a page that carries no void rows passes zero, which omits the
 * sentence rather than guessing. `named` is the invoice just voided
 * from the list, and only once Olive has read its copy.
 */

import type { ReactNode } from 'react';
import { Button } from '@/components/primitives/Button';

export interface VoidCopiesOpenBannerProps {
  /** The month's count of voided invoices with a copy still to fix in
   *  QuickBooks. Zero renders no heading. */
  count: number;
  /** How many rows on screen were last seen open WITH a payment
   *  applied. Zero omits the sentence. */
  paymentCount: number;
  /** The invoice just voided from this list whose copy Olive has since
   *  seen open, named as the first line; null otherwise. */
  named: { docNumber: string | null } | null;
  onShow: () => void;
  testId: string;
  /** The part of `count` that is the no-answer copy search (0214): voided
   *  invoices whose unanswered send may have left a copy Olive has not
   *  found yet. Never called "still open", because nobody has seen one. */
  lookupCount?: number;
}

export function VoidCopiesOpenBanner({
  count,
  paymentCount,
  named,
  onShow,
  testId,
  lookupCount = 0,
}: VoidCopiesOpenBannerProps): ReactNode {
  if (count <= 0 && named === null) return null;
  // The copies Olive has seen (or could not read) and the copies it is
  // still looking for are two claims; only the first is "still to close".
  const lookups = Math.min(Math.max(lookupCount, 0), Math.max(count, 0));
  const seen = count - lookups;
  // Singular when the month holds one, or when the only thing to say is
  // the invoice just voided and the count has not landed yet.
  const one = seen <= 1;
  return (
    <div
      data-testid={testId}
      role="alert"
      className="mb-3 space-y-1 border border-danger/40 bg-pepper-soft rounded-tight p-3"
    >
      {named !== null && (
        <p data-testid="invoice-void-copies-named" className="text-sm font-semibold text-danger">
          {named.docNumber !== null && named.docNumber !== ''
            ? `Invoice #${named.docNumber} is voided here and still open in QuickBooks.`
            : 'This invoice is voided here and still open in QuickBooks.'}
        </p>
      )}
      {seen > 0 && (
        <h2 className="text-sm font-semibold text-danger">
          {`${seen} voided ${one ? 'invoice' : 'invoices'} still to close in QuickBooks`}
        </h2>
      )}
      {(seen > 0 || named !== null) && (
        <p className="text-sm text-ink">
          {one
            ? 'QuickBooks can still charge that family until the copy is voided. Open it in QuickBooks and void it.'
            : 'QuickBooks can still charge those families until each copy is voided there.'}
        </p>
      )}
      {lookups > 0 && (
        <p data-testid="invoice-void-copies-lookup" className="text-sm font-semibold text-danger">
          {lookups === 1
            ? '1 voided invoice may still have a copy in QuickBooks. Open it to check.'
            : `${lookups} voided invoices may still have a copy in QuickBooks. Open each one to check.`}
        </p>
      )}
      {paymentCount > 0 && (
        <p data-testid="invoice-void-copies-payment" className="text-sm text-ink">
          {`${paymentCount} of them ${paymentCount === 1 ? 'has' : 'have'} a payment applied: unapply the payment in QuickBooks first, then void the copy there, or the money is left as unapplied cash.`}
        </p>
      )}
      <div className="pt-1">
        <Button variant="secondary" size="sm" data-testid="invoice-void-copies-show" onClick={onShow}>
          Show them
        </Button>
      </div>
    </div>
  );
}
